import { useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import {
  ASSET_TYPE_LABEL,
  EMBED_PROVIDER_LABEL,
  type AssetType,
  type EmbedProvider,
} from "@colab/shared";
import { formatRelative } from "@/lib/utils";
import { FileThumbnail } from "@/components/shared/file-thumbnail";
import { FileViewerDrawer } from "@/components/shared/file-viewer";
import { useLogContentView } from "../use-log-content-view";
import { EmbedDrawer } from "./embed-drawer";

// Phase 7 sub-bucket 8 — port of /app/portal/resources/ResourceCard.tsx.
// Three-way routing on click:
//   1. embed   → log view + open the embed drawer
//   2. external → log view + window.open(external_url)
//   3. file     → log view + open the inline FileViewerDrawer (renders the
//      file in-app via a log=0 signed URL, with a Download button) instead of
//      bouncing the partner out to a new tab.

export type ResourceCardAsset = {
  id: string;
  title: string;
  description: string | null;
  asset_type: AssetType;
  external_url: string | null;
  file_url: string | null;
  embed_provider: EmbedProvider | null;
  embed_url: string | null;
  categories: string[];
  updated_at: string;
};

export function ResourceCard({ asset }: { asset: ResourceCardAsset }) {
  const [embedOpen, setEmbedOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const logView = useLogContentView();

  const isEmbed = !!asset.embed_provider && !!asset.embed_url;
  const isFile = !!asset.file_url;

  function open() {
    // Fire-and-forget — logView is best-effort (silent failure by design).
    logView.mutate(asset.id);

    if (isEmbed) {
      setEmbedOpen(true);
    } else if (asset.external_url) {
      window.open(asset.external_url, "_blank", "noopener,noreferrer");
    } else if (isFile) {
      setViewerOpen(true);
    }
  }

  return (
    <>
      <div className="group flex flex-col overflow-hidden rounded-xl border bg-card transition hover:border-foreground/15">
        <div
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              open();
            }
          }}
          aria-label={`Open ${asset.title}`}
          className="cursor-pointer"
        >
          <FileThumbnail
            className="rounded-none border-0 border-b transition group-hover:opacity-95"
            asset={{
              id: asset.id,
              fileUrl: asset.file_url,
              assetType: asset.asset_type,
              embedProvider: asset.embed_provider,
              externalUrl: asset.external_url,
            }}
          />
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 font-display text-sm font-semibold tracking-tight">
              {asset.title}
            </h3>
            <span className="flex-shrink-0 rounded-md border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {asset.embed_provider
                ? EMBED_PROVIDER_LABEL[asset.embed_provider]
                : ASSET_TYPE_LABEL[asset.asset_type]}
            </span>
          </div>
          {asset.description && (
            <p className="line-clamp-3 text-xs text-muted-foreground">
              {asset.description}
            </p>
          )}
          {asset.categories.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {asset.categories.map((c) => (
                <span
                  key={c}
                  className="rounded-md border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          <div className="mt-auto flex items-center justify-between border-t pt-3 text-[11px] text-muted-foreground">
            <span>Updated {formatRelative(asset.updated_at)}</span>
            <button
              type="button"
              onClick={open}
              className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
            >
              {isEmbed ? (
                <Play className="h-3 w-3" />
              ) : (
                <ExternalLink className="h-3 w-3" />
              )}
              {isEmbed ? "Watch" : isFile ? "View" : "Open"}
            </button>
          </div>
        </div>
      </div>

      {isEmbed && (
        <EmbedDrawer
          open={embedOpen}
          onOpenChange={setEmbedOpen}
          provider={asset.embed_provider!}
          embedUrl={asset.embed_url!}
          title={asset.title}
        />
      )}
      {isFile && (
        <FileViewerDrawer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          assetId={asset.id}
          fileUrl={asset.file_url!}
          title={asset.title}
        />
      )}
    </>
  );
}
